import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/Buttons";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import IconComponent from "../../../../components/Icon/IconComponent";
import SearchField from "../../../../components/InputFields/SearchField";
import CustomMenu from "../../../../components/Menu/CustomMenu";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../../utils/constants";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
} from "../../../../utils/constants/httpConstants";
import {
  getRequest,
  postRequest,
} from "../../../../Apis/apiWrapper";
import { getErrorMessage } from "../../../../utils/helpers";
import { GREY } from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";
import GroupFormModal from "./GroupFormModal";
import DeleteGroupModal from "./DeleteGroupModal";
import MoveToGroupMenu from "./MoveToGroupMenu";
import AddMailboxesMenu from "./AddMailboxesMenu";
import CustomModal from "../../../../components/Modals/CustomModal";

const flattenInboxes = (apiDetails) => {
  const gmail = apiDetails?.inboxes?.gmail || [];
  const outlook = apiDetails?.inboxes?.outlook || [];
  const zoho = apiDetails?.inboxes?.zoho || [];
  return [...gmail, ...outlook, ...zoho]
    .filter((m) => m && m._id)
    .map((m) => ({
      id: m._id,
      email: m.email,
      // GMAIL SEND-AS ALIAS: display-only. Rows without one keep alias null
      // and render exactly as before; grouping/selection stays on `email`.
      alias: m.send_as_alias || null,
      provider: m.connection_type,
    }));
};

// what the row SHOWS: the alias when the mailbox has one picked, else the
// mailbox address (recipients see the alias, so the UI should too)
const mailboxDisplay = (m) => m?.alias || m?.email || "";

const VIEW_LIST = "list";
const VIEW_DETAIL = "detail";

const formatTimestamp = (input) => {
  if (!input) return "-";
  const d = new Date(input);
  if (isNaN(d.getTime())) return "-";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const normaliseGroups = (raw) =>
  Array.isArray(raw)
    ? raw.map((g) => ({
        _id: g._id,
        group_name: g.group_name,
        member_count: typeof g.member_count === "number" ? g.member_count : 0,
        updated_at: g.updated_at || g.created_at || null,
        // Optional; some backends return account_ids on list, others don't.
        // When present it lets the Add popover label each mailbox's current group.
        account_ids: Array.isArray(g.account_ids)
          ? g.account_ids
          : Array.isArray(g.members)
          ? g.members.map((mem) => mem.account_id).filter(Boolean)
          : [],
      }))
    : [];

const MailboxGroups = () => {
  const classes = styles();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);

  const [view, setView] = useState(VIEW_LIST);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [activeGroupDetail, setActiveGroupDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Row kebab menu state
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuTarget, setRowMenuTarget] = useState(null);

  // Toolbar / kebab Move popover anchor
  const [moveAnchor, setMoveAnchor] = useState(null);
  const [moveItems, setMoveItems] = useState([]);

  // Add-mailboxes popover anchor and target group
  const [addAnchor, setAddAnchor] = useState(null);
  const [addTargetGroupId, setAddTargetGroupId] = useState(null);

  const [formGroup, setFormGroup] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Confirmation modal before removing mailbox(es) from the current group.
  const [removeTargetIds, setRemoveTargetIds] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const fetchGroups = async () => {
    try {
      setIsLoadingGroups(true);
      const response = await getRequest(
        navigate,
        CampaignSettingsEndpoints.mailboxGroupList,
      );
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : data?.groups || [];
      setGroups(normaliseGroups(list));
    } catch (err) {
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const fetchGroupDetail = async (groupId) => {
    if (!groupId) return;
    try {
      setIsLoadingDetail(true);
      const response = await getRequest(
        navigate,
        `${CampaignSettingsEndpoints.mailboxGroupEmails}/${groupId}/emails`,
      );
      const data = response?.data?.data || {};
      setActiveGroupDetail({
        _id: data.group_id || groupId,
        group_name: data.group_name || "",
        member_count:
          typeof data.member_count === "number"
            ? data.member_count
            : (data.emails || []).length,
        emails: Array.isArray(data.emails) ? data.emails : [],
      });
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
      setActiveGroupDetail({ _id: groupId, emails: [] });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchMailboxes = async () => {
    try {
      const response = await getRequest(
        navigate,
        authEndpoints.getApiKeyDetails,
      );
      setMailboxes(flattenInboxes(response?.data?.data));
    } catch (err) {
      // Non-fatal: the rest of the screen still works without the picker pool.
      setMailboxes([]);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchMailboxes();
  }, []);

  // Best-effort: if the list endpoint omits account_ids, this map stays empty
  // and the add-mailboxes popover falls back to add-then-move on the server.
  const mailboxToGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => {
      (g.account_ids || []).forEach((aid) =>
        map.set(aid, { groupId: g._id, groupName: g.group_name }),
      );
    });
    return map;
  }, [groups]);

  // Reset search and selection whenever the view changes.
  useEffect(() => {
    setSearch("");
    setSelectedAccountIds([]);
  }, [view, activeGroupId]);

  // When entering detail view (or after a mutation while in it), refetch.
  useEffect(() => {
    if (view === VIEW_DETAIL && activeGroupId) {
      fetchGroupDetail(activeGroupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeGroupId]);

  // ─── Filtering ────────────────────────────────────────────────────
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      (g.group_name || "").toLowerCase().includes(q),
    );
  }, [groups, search]);

  const detailEmails = activeGroupDetail?.emails || [];

  // group APIs know nothing about aliases — map account email -> alias from
  // the get-api-details pool so member rows can show the sending address
  const aliasByEmail = useMemo(() => {
    const map = {};
    (mailboxes || []).forEach((m) => {
      if (m.alias) map[m.email] = m.alias;
    });
    return map;
  }, [mailboxes]);

  const filteredEmails = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return detailEmails;
    return detailEmails.filter(
      (m) =>
        (m.email || "").toLowerCase().includes(q) ||
        (aliasByEmail[m.email] || "").toLowerCase().includes(q),
    );
  }, [detailEmails, search, aliasByEmail]);

  // ─── Selection helpers ────────────────────────────────────────────
  const toggleAccount = (id) => {
    setSelectedAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (selectedAccountIds.length === filteredEmails.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(filteredEmails.map((m) => m.account_id));
    }
  };

  const allChecked =
    filteredEmails.length > 0 &&
    selectedAccountIds.length === filteredEmails.length;
  const partialChecked =
    selectedAccountIds.length > 0 &&
    selectedAccountIds.length < filteredEmails.length;

  // ─── Mutations ────────────────────────────────────────────────────
  // Opens the confirmation modal; the actual removal runs from `confirmRemove`.
  const handleRemoveSelected = (ids) => {
    if (!ids?.length) return;
    setRemoveTargetIds(ids);
  };

  const confirmRemove = async () => {
    const ids = removeTargetIds || [];
    if (!ids.length) return;
    try {
      setIsRemoving(true);
      const results = await Promise.allSettled(
        ids.map((account_id) =>
          postRequest(
            navigate,
            CampaignSettingsEndpoints.mailboxGroupRemoveEmail,
            { account_id, group_id: activeGroupId },
          ),
        ),
      );
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failures = results.filter((r) => r.status === "rejected");
      if (successCount > 0) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          `${successCount} mailbox${successCount > 1 ? "es" : ""} removed`,
        );
      }
      if (failures.length > 0) {
        const first = failures[0].reason;
        EventEmitter.emit(
          EMITTER_ERROR,
          `${failures.length} couldn't be removed: ${getErrorMessage(
            first?.data?.data?.errors || {},
            first?.data?.message,
          )}`,
        );
      }
      setSelectedAccountIds([]);
      fetchGroups();
      fetchGroupDetail(activeGroupId);
      setRemoveTargetIds(null);
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const openCreate = () => {
    setFormGroup(null);
    setFormOpen(true);
  };

  const openKebab = (e, target) => {
    e.stopPropagation();
    setRowMenuAnchor(e.currentTarget);
    setRowMenuTarget(target);
  };

  const closeKebab = () => {
    setRowMenuAnchor(null);
    setRowMenuTarget(null);
  };

  // Toolbar Move: every selected mailbox lives in the current group.
  const openToolbarMove = (e) => {
    setMoveItems(
      selectedAccountIds.map((id) => ({
        account_id: id,
        fromGroupId: activeGroupId,
      })),
    );
    setMoveAnchor(e.currentTarget);
  };

  // Kebab Move: single mailbox from the current group.
  const openRowMove = (mailbox) => {
    setMoveItems([
      { account_id: mailbox.account_id, fromGroupId: activeGroupId },
    ]);
    setMoveAnchor(rowMenuAnchor);
  };

  // Open Add-mailboxes popover for a specific group, anchored to the trigger.
  const openAddMailboxes = (e, groupId) => {
    setAddTargetGroupId(groupId);
    setAddAnchor(e?.currentTarget || rowMenuAnchor);
  };

  const handleAdded = () => {
    fetchGroups();
    if (view === VIEW_DETAIL && addTargetGroupId === activeGroupId) {
      fetchGroupDetail(activeGroupId);
    }
  };

  const handleGroupSaved = () => fetchGroups();

  const handleGroupDeleted = (deletedId) => {
    if (view === VIEW_DETAIL && activeGroupId === deletedId) {
      setView(VIEW_LIST);
      setActiveGroupId(null);
      setActiveGroupDetail(null);
    }
    fetchGroups();
  };

  const handleMoved = () => {
    setSelectedAccountIds([]);
    setMoveItems([]);
    fetchGroups();
    if (view === VIEW_DETAIL) fetchGroupDetail(activeGroupId);
  };

  const enterDetail = (group) => {
    setActiveGroupId(group._id);
    setActiveGroupDetail({ ...group, emails: [] });
    setView(VIEW_DETAIL);
  };

  // ─── Kebab item builders ──────────────────────────────────────────
  const listKebabItems = (group) => [
    {
      id: "add",
      label: STRINGS.ADD_MAILBOXES,
      iconLabel: ICON_LABELS.ADD,
      function: () => openAddMailboxes(null, group._id),
    },
    {
      id: "rename",
      label: STRINGS.RENAME_GROUP,
      iconLabel: ICON_LABELS.EDIT,
      function: () => {
        setFormGroup(group);
        setFormOpen(true);
      },
    },
    {
      id: "delete",
      label: STRINGS.DELETE,
      iconLabel: ICON_LABELS.DELETE,
      variant: "error",
      function: () => setDeleteTarget(group),
    },
  ];

  const detailKebabItems = (mailbox) => [
    {
      id: "move",
      label: STRINGS.ROW_MOVE_TO_OTHER,
      iconLabel: ICON_LABELS.ARROW_FORWARD,
      function: () => openRowMove(mailbox),
    },
    {
      id: "remove",
      label: STRINGS.REMOVE_FROM_GROUP,
      iconLabel: ICON_LABELS.DELETE,
      variant: "error",
      function: () => handleRemoveSelected([mailbox.account_id]),
    },
  ];

  const kebabItems = useMemo(() => {
    if (!rowMenuTarget) return [];
    if (rowMenuTarget.kind === "group") {
      return listKebabItems(rowMenuTarget.group);
    }
    return detailKebabItems(rowMenuTarget.mailbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowMenuTarget, rowMenuAnchor]);

  // ─── List view ────────────────────────────────────────────────────
  const renderListView = () => {
    const gridTemplateColumns =
      "minmax(220px, 2fr) minmax(120px, 0.8fr) minmax(140px, 1fr) 56px";

    return (
      <>
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.GROUPS_TAB_HINT}
        />
        <Box className={classes.mgToolbar}>
          <Box className={classes.mgToolbarLeft}>
            <SearchField
              size={"sm36"}
              autoComplete="off"
              placeholder={STRINGS.GROUPS_SEARCH_PLACEHOLDER}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box className={classes.mgToolbarRight}>
            <Button
              label={STRINGS.CREATE_GROUP}
              LeftIcon={ICON_LABELS.ADD}
              variant={"primary"}
              size={"sm36"}
              onClick={openCreate}
            />
          </Box>
        </Box>

        {!isLoadingGroups && filteredGroups.length === 0 ? (
          <Box className={classes.mgEmptyState}>
            <IconComponent
              iconLabel={ICON_LABELS.FOLDER}
              color={GREY.TERTIARY}
              fontSize={"28px"}
            />
            <TypographyInline
              size={"md"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label={
                search ? STRINGS.NO_GROUPS_MATCH : STRINGS.GROUPS_EMPTY_TITLE
              }
            />
            {!search && (
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={STRINGS.GROUPS_EMPTY_DESC}
              />
            )}
          </Box>
        ) : (
          <Box className={classes.blkTableShell}>
            <Box
              className={classes.blkTableHead}
              style={{ gridTemplateColumns }}
            >
              <span className={classes.blkTableHeadCell}>
                {STRINGS.GROUP_NAME}
              </span>
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_MEMBERS}
              </span>
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_UPDATED_AT}
              </span>
              <span className={classes.blkTableHeadCell} />
            </Box>
            <Box className={classes.blkTableBody}>
              {isLoadingGroups && filteredGroups.length === 0
                ? [1, 2, 3].map((n) => (
                    <Box
                      key={`grp-skel-${n}`}
                      className={classes.blkTableRow}
                      style={{ gridTemplateColumns }}
                    >
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"60%"}
                        skeletonHeight={14}
                      />
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"40%"}
                        skeletonHeight={14}
                      />
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"50%"}
                        skeletonHeight={14}
                      />
                      <span />
                    </Box>
                  ))
                : null}
              {filteredGroups.map((g) => (
                <Box
                  key={g._id}
                  className={classes.blkTableRow}
                  style={{ gridTemplateColumns }}
                >
                  <span
                    className={`${classes.blkTableCell} ${classes.mgRowName}`}
                    title={g.group_name}
                    onClick={() => enterDetail(g)}
                  >
                    {g.group_name}
                  </span>
                  <span className={classes.blkTableCell}>
                    <span className={classes.mgCountChip}>
                      {g.member_count}
                    </span>
                  </span>
                  <span className={classes.blkTableCell}>
                    {formatTimestamp(g.updated_at)}
                  </span>
                  <span className={classes.blkTableCell}>
                    <Box
                      className={classes.mgKebab}
                      onClick={(e) =>
                        openKebab(e, { kind: "group", group: g })
                      }
                    >
                      <IconComponent
                        iconLabel={ICON_LABELS.MORE_VERT}
                        fontSize={"18px"}
                        color={GREY.SECONDARY}
                      />
                    </Box>
                  </span>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  };

  // ─── Detail view ──────────────────────────────────────────────────
  const renderDetailView = () => {
    const gridTemplateColumns =
      "32px minmax(220px, 1.8fr) minmax(180px, 1.4fr) minmax(120px, 0.8fr) 56px";

    return (
      <>
        <Box className={classes.mgBreadcrumb}>
          <button
            type="button"
            className={classes.mgBreadcrumbLink}
            onClick={() => {
              setView(VIEW_LIST);
              setActiveGroupId(null);
              setActiveGroupDetail(null);
            }}
          >
            {STRINGS.GROUPS_BREADCRUMB}
          </button>
          <IconComponent
            iconLabel={ICON_LABELS.KEYBOARD_ARROW_RIGHT}
            fontSize={"16px"}
            color={GREY.TERTIARY}
          />
          <span className={classes.mgBreadcrumbCurrent}>
            {activeGroupDetail?.group_name || "-"}
          </span>
          <span className={classes.mgCountChip}>
            {activeGroupDetail?.member_count || 0}
          </span>
        </Box>

        <Box className={classes.mgToolbar}>
          <Box className={classes.mgToolbarLeft}>
            <SearchField
              size={"sm36"}
              autoComplete="off"
              placeholder={STRINGS.MAILBOXES_SEARCH_PLACEHOLDER}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box className={classes.mgToolbarRight}>
            <Button
              label={STRINGS.REMOVE_FROM_GROUP}
              LeftIcon={ICON_LABELS.DELETE}
              variant={"ghost"}
              size={"sm36"}
              disabled={selectedAccountIds.length === 0}
              onClick={() => handleRemoveSelected(selectedAccountIds)}
            />
            <Button
              label={
                selectedAccountIds.length > 0
                  ? `${STRINGS.MOVE_TO} (${selectedAccountIds.length})`
                  : STRINGS.MOVE_TO
              }
              LeftIcon={ICON_LABELS.DRIVE_FILE_MOVE}
              variant={"ghost"}
              size={"sm36"}
              disabled={selectedAccountIds.length === 0}
              onClick={openToolbarMove}
            />
            <Button
              label={STRINGS.ADD_MAILBOXES}
              LeftIcon={ICON_LABELS.ADD}
              variant={"primary"}
              size={"sm36"}
              onClick={(e) => openAddMailboxes(e, activeGroupId)}
            />
          </Box>
        </Box>

        {!isLoadingDetail && filteredEmails.length === 0 ? (
          <Box className={classes.mgEmptyState}>
            <IconComponent
              iconLabel={ICON_LABELS.EMAIL}
              color={GREY.TERTIARY}
              fontSize={"28px"}
            />
            <TypographyInline
              size={"md"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label={
                search
                  ? STRINGS.NO_MAILBOXES_MATCH
                  : STRINGS.GROUP_EMPTY_TITLE
              }
            />
            {!search && (
              <>
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={STRINGS.GROUP_EMPTY_DESC}
                />
                <Box sx={{ marginTop: "8px" }}>
                  <Button
                    label={STRINGS.ADD_MAILBOXES}
                    LeftIcon={ICON_LABELS.ADD}
                    variant={"primary"}
                    size={"sm36"}
                    onClick={(e) => openAddMailboxes(e, activeGroupId)}
                  />
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box className={classes.blkTableShell}>
            <Box
              className={classes.blkTableHead}
              style={{ gridTemplateColumns }}
            >
              <span className={classes.mgCheckboxCell}>
                <Checkbox
                  checked={allChecked}
                  partial={partialChecked}
                  onChange={toggleAll}
                  noMargin
                />
              </span>
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_MAILBOX}
              </span>
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_DEFAULT_SIGNATURE}
              </span>
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_UPDATED_AT}
              </span>
              <span className={classes.blkTableHeadCell} />
            </Box>
            <Box className={classes.blkTableBody}>
              {isLoadingDetail && filteredEmails.length === 0
                ? [1, 2, 3].map((n) => (
                    <Box
                      key={`mb-skel-${n}`}
                      className={classes.blkTableRow}
                      style={{ gridTemplateColumns }}
                    >
                      <span />
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"70%"}
                        skeletonHeight={14}
                      />
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"50%"}
                        skeletonHeight={14}
                      />
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"40%"}
                        skeletonHeight={14}
                      />
                      <span />
                    </Box>
                  ))
                : null}
              {filteredEmails.map((m) => {
                const isChecked = selectedAccountIds.includes(m.account_id);
                const sigName =
                  m.default_signature?.signature_name ||
                  m.default_signature?.name ||
                  "-";
                return (
                  <Box
                    key={m.account_id}
                    className={classes.blkTableRow}
                    style={{ gridTemplateColumns }}
                  >
                    <span className={classes.mgCheckboxCell}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleAccount(m.account_id)}
                        noMargin
                      />
                    </span>
                    <span
                      className={`${classes.blkTableCell} ${classes.blkTableCellName}`}
                      title={aliasByEmail[m.email] ? `Mailbox: ${m.email}` : m.email}
                    >
                      {aliasByEmail[m.email] || m.email}
                    </span>
                    <span className={classes.blkTableCell} title={sigName}>
                      {sigName}
                    </span>
                    <span className={classes.blkTableCell}>
                      {formatTimestamp(m.added_at)}
                    </span>
                    <span className={classes.blkTableCell}>
                      <Box
                        className={classes.mgKebab}
                        onClick={(e) =>
                          openKebab(e, { kind: "mailbox", mailbox: m })
                        }
                      >
                        <IconComponent
                          iconLabel={ICON_LABELS.MORE_VERT}
                          fontSize={"18px"}
                          color={GREY.SECONDARY}
                        />
                      </Box>
                    </span>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box className={classes.mgPage}>
      {view === VIEW_LIST ? renderListView() : renderDetailView()}

      <CustomMenu
        items={kebabItems}
        open={Boolean(rowMenuAnchor)}
        anchor={rowMenuAnchor}
        onClose={closeKebab}
        rowData={
          rowMenuTarget?.kind === "mailbox" ? rowMenuTarget.mailbox : {}
        }
      />

      {Boolean(moveAnchor) && (
        <MoveToGroupMenu
          anchorEl={moveAnchor}
          open={Boolean(moveAnchor)}
          onClose={() => {
            setMoveAnchor(null);
            setMoveItems([]);
          }}
          onMoved={handleMoved}
          onCreateGroupClick={openCreate}
          items={moveItems}
          currentGroupId={activeGroupId}
          groups={groups}
        />
      )}

      {Boolean(addAnchor) && (
        <AddMailboxesMenu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          onClose={() => {
            setAddAnchor(null);
            setAddTargetGroupId(null);
          }}
          onAdded={handleAdded}
          mailboxes={mailboxes}
          mailboxToGroup={mailboxToGroup}
          targetGroupId={addTargetGroupId}
        />
      )}

      {formOpen && (
        <GroupFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            handleGroupSaved();
            if (view === VIEW_DETAIL) fetchGroupDetail(activeGroupId);
          }}
          group={formGroup}
          existingNames={groups.map((g) => ({
            _id: g._id,
            group_name: g.group_name,
          }))}
        />
      )}

      {deleteTarget && (
        <DeleteGroupModal
          open={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleGroupDeleted}
          group={deleteTarget}
        />
      )}

      {removeTargetIds && (
        <CustomModal
          open={Boolean(removeTargetIds)}
          close={() => !isRemoving && setRemoveTargetIds(null)}
          onClose={() => !isRemoving && setRemoveTargetIds(null)}
          variant={"alert"}
          width="420px"
          header={{
            heading: {
              id: "remove-from-group",
              label: STRINGS.REMOVE_FROM_GROUP_TITLE,
            },
            alertIcon: {
              id: "remove-icon",
              label: ICON_LABELS.DELETE,
            },
          }}
          children={
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <TypographyInline
                size={"md"}
                color={GREY.PRIMARY}
                fontWeight={fontWeight.SEMIBOLD}
                label={
                  removeTargetIds.length > 1
                    ? `Remove ${removeTargetIds.length} mailboxes?`
                    : "Remove this mailbox?"
                }
              />
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={
                  removeTargetIds.length > 1
                    ? STRINGS.REMOVE_FROM_GROUP_CONFIRM_MANY
                    : STRINGS.REMOVE_FROM_GROUP_CONFIRM_ONE
                }
              />
            </Box>
          }
          footer={{
            left: <div />,
            right: (
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Button
                  label={STRINGS.CANCEL}
                  size={"sm36"}
                  variant={"ghost"}
                  onClick={() => setRemoveTargetIds(null)}
                  disabled={isRemoving}
                />
                <Button
                  label={STRINGS.REMOVE_FROM_GROUP}
                  size={"sm36"}
                  variant={"primary"}
                  onClick={confirmRemove}
                  isLoading={isRemoving}
                />
              </Box>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default MailboxGroups;
